import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Phone, Mail, MapPin, Clock, Star,
  Wheat, Cake, Croissant, Heart, Menu, X, Check,
  Sparkles, ChevronLeft, ChevronRight, Send,
} from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_QUICHE = {
  Mon: { name: 'Leek & Gruyère', desc: 'Slow-melted leeks, aged Swiss gruyère, butter pastry.' },
  Tue: { name: 'Mushroom-Thyme', desc: 'Cremini, oyster, and shiitake with fresh thyme & cream.' },
  Wed: { name: 'Leek & Gruyère', desc: 'Slow-melted leeks, aged Swiss gruyère, butter pastry.' },
  Thu: { name: 'Mushroom-Thyme', desc: 'Cremini, oyster, and shiitake with fresh thyme & cream.' },
  Fri: { name: 'Bacon-Cheddar', desc: 'Cured bacon, sharp white cheddar, scallion, fresh chive.' },
  Sat: { name: 'Brunch Special', desc: 'Whatever the chef pulls together — usually phenomenal.' },
  Sun: { name: 'Brunch Special', desc: 'Whatever the chef pulls together — usually phenomenal.' },
};

const BAKE_SCHEDULE = [
  { hour: 5,  label: 'Country sourdough into the deck oven' },
  { hour: 6,  label: 'Croissants out of the proofer' },
  { hour: 7,  label: 'Pain au chocolat hits the case' },
  { hour: 8,  label: 'Sticky buns, warm and gooey' },
  { hour: 10, label: 'Almond croissants, twice-baked' },
  { hour: 12, label: 'Fresh quiche of the day' },
  { hour: 15, label: 'Afternoon olive fougasse' },
];

const CAKE_FLAVORS = [
  { id: 'vanilla',   label: 'Brown Butter Vanilla', color: '#F5E6C8' },
  { id: 'chocolate', label: 'Dark Cocoa',           color: '#5A3A2A' },
  { id: 'lemon',     label: 'Lemon Olive Oil',      color: '#F4E27A' },
  { id: 'almond',    label: 'Almond Frangipane',    color: '#E8D4B0' },
];

const CAKE_FROSTINGS = [
  { id: 'buttercream',  label: 'Swiss Buttercream',  color: '#FAF1E0' },
  { id: 'creamcheese',  label: 'Cream Cheese',       color: '#FFF8E8' },
  { id: 'ganache',      label: 'Dark Ganache',       color: '#3B2418' },
  { id: 'mascarpone',   label: 'Mascarpone Cream',   color: '#FFFBF0' },
];

const CSA_LOAVES = [
  { id: 'sourdough', name: 'Country Sourdough',   price: 8 },
  { id: 'baguette',  name: 'Baguette',            price: 5 },
  { id: 'rye',       name: 'Seeded Rye',          price: 9 },
  { id: 'walnut',    name: 'Walnut-Levain',       price: 10 },
  { id: 'whole',     name: 'Whole Wheat Country', price: 8 },
];

const STYLE = `
  .bdb-paper {
    background-color: #FAF6EE;
    background-image:
      radial-gradient(rgba(122,104,86,0.06) 1px, transparent 1px),
      radial-gradient(rgba(122,104,86,0.04) 1px, transparent 1px);
    background-size: 22px 22px, 38px 38px;
    background-position: 0 0, 11px 19px;
  }
  .bdb-grain::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(154,52,18,0.06), transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(217,119,6,0.05), transparent 45%);
    pointer-events: none;
  }
  @keyframes bdb-rise {
    0%, 100% { transform: translateY(0) scale(1); }
    50%      { transform: translateY(-3px) scale(1.04); }
  }
  .bdb-rise { animation: bdb-rise 4.5s ease-in-out infinite; transform-origin: center bottom; }
  @keyframes bdb-bubble-pop {
    0%   { transform: scale(0.4); opacity: 0; }
    20%  { opacity: 0.7; }
    100% { transform: scale(1.4); opacity: 0; }
  }
  .bdb-bubble { animation: bdb-bubble-pop 3.2s ease-out infinite; }
  @keyframes bdb-steam {
    0%   { transform: translateY(0) translateX(0); opacity: 0; }
    20%  { opacity: 0.5; }
    100% { transform: translateY(-30px) translateX(8px); opacity: 0; }
  }
  .bdb-steam { animation: bdb-steam 2.6s ease-out infinite; }
  @keyframes bdb-flour {
    0%, 100% { transform: translate(0, 0); }
    50%      { transform: translate(2px, -2px); }
  }
  .bdb-flour { animation: bdb-flour 6s ease-in-out infinite; }
  .bdb-handwrite { font-family: 'Caveat', 'Brush Script MT', cursive; }
  .bdb-display { font-family: 'Fraunces', 'Times New Roman', serif; font-optical-sizing: auto; }
  .bdb-pull-quote::before, .bdb-pull-quote::after {
    font-family: 'Fraunces', serif;
    font-weight: 800;
    color: #9A3412;
    line-height: 0;
    font-size: 4em;
    vertical-align: -0.4em;
  }
  .bdb-pull-quote::before { content: '\\201C'; margin-right: 0.1em; }
  .bdb-pull-quote::after  { content: '\\201D'; margin-left: 0.1em; }
  @keyframes bdb-tape-flutter {
    0%, 100% { transform: rotate(-3deg) translateY(0); }
    50%      { transform: rotate(-2deg) translateY(-1px); }
  }
  .bdb-stamp {
    border: 2px solid #9A3412;
    color: #9A3412;
    font-family: 'Fraunces', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    transform: rotate(-6deg);
    border-radius: 4px;
    padding: 6px 14px;
    display: inline-block;
    box-shadow: inset 0 0 0 1px #9A3412;
  }
  @media (prefers-reduced-motion: reduce) {
    .bdb-rise, .bdb-bubble, .bdb-steam, .bdb-flour { animation: none !important; }
  }
`;

function getNow() {
  return new Date();
}

function nextBakeFrom(now) {
  const h = now.getHours() + now.getMinutes() / 60;
  const upcoming = BAKE_SCHEDULE.find((b) => b.hour > h);
  if (upcoming) return { kind: 'upcoming', ...upcoming };
  return { kind: 'tomorrow', ...BAKE_SCHEDULE[0] };
}

function lastBakeFrom(now) {
  const h = now.getHours() + now.getMinutes() / 60;
  const past = [...BAKE_SCHEDULE].reverse().find((b) => b.hour <= h);
  return past || null;
}

function BlueDoorBakeryDemo({ demo, sample = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [now, setNow] = useState(() => getNow());

  useEffect(() => {
    const id = setInterval(() => setNow(getNow()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');
  const todayName = DAYS[now.getDay()];
  const todayQuiche = DAY_QUICHE[todayName];
  const nextBake = useMemo(() => nextBakeFrom(now), [now]);
  const lastBake = useMemo(() => lastBakeFrom(now), [now]);

  return (
    <div className="bdb-paper text-[#23170E] min-h-screen" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{STYLE}</style>

      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />
      <Masthead
        demo={demo}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        phoneRaw={phoneRaw}
      />

      <Hero demo={demo} now={now} nextBake={nextBake} lastBake={lastBake} />

      <Hilde now={now} />

      <TodaysCase featured={demo.featured} todayName={todayName} todayQuiche={todayQuiche} />

      <StorySpread about={demo.about} cover={demo.gallery?.[1] || demo.cover} />

      <CakeStudio />

      <BreadCSA />

      <GalleryStrip gallery={demo.gallery} />

      <Polaroids testimonials={demo.testimonials} />

      <Visit demo={demo} phoneRaw={phoneRaw} />

      <Footer demo={demo} phoneRaw={phoneRaw} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function Masthead({ demo, scrolled, navOpen, setNavOpen, phoneRaw }) {
  const links = [
    { href: '#hilde', label: 'Our Starter' },
    { href: '#case', label: 'The Case' },
    { href: '#story', label: 'Our Story' },
    { href: '#cakes', label: 'Cake Studio' },
    { href: '#csa', label: 'Bread CSA' },
    { href: '#visit', label: 'Visit' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-[#FAF6EE]/95 backdrop-blur shadow-sm border-b border-[#E5DCC8]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5">
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center text-[#FAF6EE] font-bold"
            style={{ background: '#9A3412' }}
            aria-hidden="true"
          >
            <Wheat size={18} />
          </span>
          <div className="leading-tight">
            <div className="bdb-display font-bold text-lg md:text-xl tracking-tight" style={{ color: '#23170E' }}>
              {demo.name}
            </div>
            <div className="bdb-handwrite text-sm -mt-1" style={{ color: '#9A3412' }}>
              est. 2014
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-6 bdb-display text-sm font-medium" style={{ color: '#3D2918' }}>
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group hover:text-[#9A3412] transition-colors">
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-[#9A3412] transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phoneRaw}`}
            className="bdb-display text-sm font-semibold hover:text-[#9A3412] transition-colors inline-flex items-center gap-1.5"
          >
            <Phone size={14} /> {demo.contact.phone}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-[#E5DCC8] bg-[#FAF6EE]"
          >
            <div className="px-4 py-4 flex flex-col gap-1 bdb-display">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-medium text-[#3D2918] hover:bg-[#F1E8D4]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`tel:${phoneRaw}`}
                className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-white"
                style={{ background: '#9A3412' }}
              >
                <Phone size={14} /> {demo.contact.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero({ demo, now, nextBake, lastBake }) {
  const reduce = useReducedMotion();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end start'] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);

  const minutesAgo = lastBake ? Math.round(((now.getHours() + now.getMinutes() / 60) - lastBake.hour) * 60) : 0;
  const minutesUntil = nextBake.kind === 'upcoming'
    ? Math.round((nextBake.hour - (now.getHours() + now.getMinutes() / 60)) * 60)
    : Math.round((24 - (now.getHours() + now.getMinutes() / 60) + nextBake.hour) * 60);

  return (
    <section id="top" ref={containerRef} className="relative bdb-grain pt-8 md:pt-14 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full opacity-40 pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(217,119,6,0.25), transparent 70%)' }} />
      <div className="absolute top-40 -left-10 w-[280px] h-[280px] rounded-full opacity-30 pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(154,52,18,0.2), transparent 70%)' }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-center gap-3 mb-6 text-xs uppercase tracking-[0.25em]" style={{ color: '#9A3412' }}>
          <span className="h-px w-10 bg-[#9A3412]/50" />
          <span className="bdb-display font-semibold">{demo.location} · since 2014</span>
          <span className="h-px w-10 bg-[#9A3412]/50" />
        </div>

        <h1 className="bdb-display font-black text-5xl sm:text-6xl md:text-8xl lg:text-[7.5rem] leading-[0.92] text-center tracking-tight" style={{ color: '#23170E' }}>
          Butter,<br />
          <span className="italic font-medium" style={{ color: '#9A3412' }}>time, </span>
          <span className="bdb-handwrite font-medium text-[#9A3412] inline-block -rotate-2 -translate-y-2" style={{ fontSize: '0.85em' }}>
            &amp;
          </span>
          <br />
          good company.
        </h1>

        <p className="text-center max-w-2xl mx-auto mt-6 md:mt-8 text-base md:text-lg leading-relaxed" style={{ color: '#5C4633' }}>
          A neighborhood bakery, the way they used to be. Naturally-leavened breads, hand-laminated pastries,
          and the seat by the window is always free if you bring a book.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="#case"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
            style={{ background: '#9A3412', fontFamily: 'Fraunces, serif' }}
          >
            See today's case <ArrowRight size={16} />
          </a>
          <a
            href="#cakes"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border-2 transition-all hover:bg-[#9A3412] hover:text-white"
            style={{ borderColor: '#9A3412', color: '#9A3412', fontFamily: 'Fraunces, serif' }}
          >
            <Cake size={16} /> Order a custom cake
          </a>
        </div>

        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-10 items-center mt-14 md:mt-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="md:text-right space-y-3"
          >
            <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: '#9A3412' }}>
              Just out of the oven
            </p>
            {lastBake && (
              <p className="bdb-display text-2xl md:text-3xl leading-tight italic" style={{ color: '#23170E' }}>
                {lastBake.label.charAt(0).toLowerCase() + lastBake.label.slice(1)}
              </p>
            )}
            <p className="bdb-handwrite text-xl" style={{ color: '#9A3412' }}>
              {minutesAgo <= 0 ? 'just now' : `${minutesAgo} min ago`}
            </p>
          </motion.div>

          <motion.div
            style={{ y: heroImgY }}
            className="relative mx-auto"
          >
            <div className="relative w-64 h-64 md:w-80 md:h-80">
              <div
                className="absolute inset-0 rounded-full overflow-hidden shadow-2xl border-[6px] border-[#FAF6EE]"
                style={{ boxShadow: '0 30px 60px -20px rgba(35,23,14,0.4)' }}
              >
                <img src={demo.cover} alt="" className="w-full h-full object-cover" fetchpriority="high" />
              </div>
              <span
                className="absolute -top-3 -right-2 bdb-stamp text-xs"
                style={{ animation: reduce ? 'none' : 'bdb-tape-flutter 6s ease-in-out infinite' }}
              >
                Fresh today
              </span>
              <SteamPuffs />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="md:text-left space-y-3"
          >
            <p className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: '#9A3412' }}>
              Up next
            </p>
            <p className="bdb-display text-2xl md:text-3xl leading-tight italic" style={{ color: '#23170E' }}>
              {nextBake.label.charAt(0).toLowerCase() + nextBake.label.slice(1)}
            </p>
            <p className="bdb-handwrite text-xl" style={{ color: '#9A3412' }}>
              in about {minutesUntil < 60 ? `${minutesUntil} min` : `${Math.floor(minutesUntil / 60)}h ${minutesUntil % 60}m`}
              {nextBake.kind === 'tomorrow' && ' (tomorrow)'}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SteamPuffs() {
  return (
    <div className="absolute inset-x-0 top-0 h-12 pointer-events-none flex justify-center" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="bdb-steam absolute block w-3 h-3 rounded-full"
          style={{
            left: `calc(50% + ${(i - 1) * 14}px)`,
            top: '-12px',
            background: 'rgba(255,255,255,0.7)',
            filter: 'blur(2px)',
            animationDelay: `${i * 0.7}s`,
          }}
        />
      ))}
    </div>
  );
}

function Hilde({ now }) {
  // 24-hour cold ferment cycle visualization. Stage progresses through
  // hours of the day for fun — not a literal mapping, just charm.
  const hour = now.getHours() + now.getMinutes() / 60;
  const cycleProgress = (hour / 24);
  const stages = [
    { from: 0,  to: 6,  label: 'Cold rest',     desc: 'Hilde\'s in the walk-in. Slow and steady.' },
    { from: 6,  to: 10, label: 'Warming up',    desc: 'Out of the cold. Coming back to life.' },
    { from: 10, to: 16, label: 'Active rise',   desc: 'Bubbles forming. The kitchen smells like apples.' },
    { from: 16, to: 21, label: 'Peak ferment',  desc: 'Doubled, domed, and singing.' },
    { from: 21, to: 24, label: 'Refresh & feed',desc: 'Tonight\'s feeding. Ready for tomorrow.' },
  ];
  const stage = stages.find((s) => hour >= s.from && hour < s.to) || stages[0];
  const reduce = useReducedMotion();

  return (
    <section id="hilde" className="relative py-16 md:py-24 bdb-grain">
      <div className="max-w-6xl mx-auto px-4 md:px-6 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="order-2 lg:order-1"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ color: '#9A3412' }}>
            Meet Hilde
          </p>
          <h2 className="bdb-display font-black text-4xl md:text-5xl leading-[1.05] mb-5" style={{ color: '#23170E' }}>
            Our sourdough <span className="italic font-medium" style={{ color: '#9A3412' }}>starter</span>,
            <br />
            <span className="bdb-handwrite font-medium" style={{ color: '#9A3412', fontSize: '1.1em' }}>
              ten years old this spring.
            </span>
          </h2>
          <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#5C4633' }}>
            Every loaf we sell is built on a 24-hour cold ferment. Hilde does most of the heavy
            lifting — wild yeast, rye flour, time, and a stainless steel home in the walk-in.
            She's a member of staff at this point. Right now, she's:
          </p>

          <div className="rounded-2xl p-5 border-2 border-dashed" style={{ borderColor: '#9A3412', background: '#FFFBF2' }}>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="bdb-display font-bold text-2xl" style={{ color: '#9A3412' }}>{stage.label}</span>
              <span className="bdb-handwrite text-base" style={{ color: '#7A6856' }}>
                · {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            <p className="bdb-display italic text-lg" style={{ color: '#3D2918' }}>{stage.desc}</p>
          </div>

          <div className="mt-6 flex items-start gap-3">
            <Sparkles size={18} className="flex-shrink-0 mt-1" style={{ color: '#D97706' }} />
            <p className="text-sm leading-relaxed" style={{ color: '#5C4633' }}>
              <strong className="bdb-display">Long ferment, short ingredient list.</strong> Just flour, water,
              salt, and Hilde — no commercial yeast, no shortcuts. The crumb is open and chewy. The crust crackles.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="order-1 lg:order-2 relative flex justify-center"
        >
          <HildeJar progress={cycleProgress} reduce={reduce} />
        </motion.div>
      </div>
    </section>
  );
}

function HildeJar({ progress, reduce }) {
  // SVG mason jar with an animated dough mass that rises and bubbles.
  // The fill height tracks the daily progress for a slow, ambient effect.
  const fillHeight = 60 + Math.sin(progress * Math.PI * 2) * 18;

  return (
    <div className="relative w-[280px] h-[360px] md:w-[340px] md:h-[440px]">
      <svg viewBox="0 0 280 360" className="w-full h-full" aria-label="A mason jar holding sourdough starter, slowly rising and falling">
        <defs>
          <linearGradient id="bdb-glass" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.05)" />
            <stop offset="50%"  stopColor="rgba(255,255,255,0.18)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
          </linearGradient>
          <linearGradient id="bdb-dough" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#F4E4C4" />
            <stop offset="100%" stopColor="#E0CBA0" />
          </linearGradient>
          <clipPath id="bdb-jar-clip">
            <path d="M 70 120 L 70 320 Q 70 340 90 340 L 190 340 Q 210 340 210 320 L 210 120 Z" />
          </clipPath>
        </defs>

        <ellipse cx="140" cy="350" rx="80" ry="6" fill="rgba(35,23,14,0.12)" />

        <rect x="80" y="60" width="120" height="20" rx="3" fill="#7A4A2A" />
        <rect x="78" y="56" width="124" height="8" rx="2" fill="#5C3920" />
        <rect x="86" y="64" width="108" height="2" fill="rgba(255,255,255,0.2)" />

        <path
          d="M 70 120 L 70 320 Q 70 340 90 340 L 190 340 Q 210 340 210 320 L 210 120 L 200 80 L 80 80 Z"
          fill="url(#bdb-glass)"
          stroke="rgba(122,104,86,0.3)"
          strokeWidth="2"
        />

        <g clipPath="url(#bdb-jar-clip)">
          <g className={reduce ? '' : 'bdb-rise'} style={{ transformOrigin: '140px 340px' }}>
            <path
              d={`M 70 ${340 - fillHeight - 8}
                  Q 90 ${340 - fillHeight - 14} 110 ${340 - fillHeight - 6}
                  T 150 ${340 - fillHeight - 10}
                  T 190 ${340 - fillHeight - 4}
                  T 210 ${340 - fillHeight - 8}
                  L 210 340 L 70 340 Z`}
              fill="url(#bdb-dough)"
            />
            {[
              { cx: 100, cy: 280, r: 4 },
              { cx: 130, cy: 300, r: 3 },
              { cx: 160, cy: 270, r: 5 },
              { cx: 180, cy: 310, r: 3 },
              { cx: 115, cy: 320, r: 2.5 },
            ].map((b, i) => (
              <circle
                key={i}
                cx={b.cx}
                cy={b.cy}
                r={b.r}
                fill="rgba(255,255,255,0.45)"
                className={reduce ? '' : 'bdb-bubble'}
                style={{ animationDelay: `${i * 0.6}s`, transformOrigin: `${b.cx}px ${b.cy}px` }}
              />
            ))}
          </g>
        </g>

        <rect x="86" y="170" width="108" height="60" rx="4" fill="#FAF6EE" stroke="#9A3412" strokeWidth="1.5" />
        <text x="140" y="190" textAnchor="middle" fontSize="10" fontFamily="Fraunces, serif" fontWeight="700" fill="#9A3412" letterSpacing="2">
          HILDE
        </text>
        <text x="140" y="206" textAnchor="middle" fontSize="9" fontFamily="Caveat, cursive" fill="#23170E">
          fed daily, please
        </text>
        <text x="140" y="222" textAnchor="middle" fontSize="9" fontFamily="Caveat, cursive" fill="#23170E">
          since March 2014
        </text>

        <line x1="78" y1="100" x2="86" y2="100" stroke="rgba(122,104,86,0.4)" strokeWidth="1" />
        <line x1="78" y1="140" x2="86" y2="140" stroke="rgba(122,104,86,0.4)" strokeWidth="1" />
      </svg>

      <div className="absolute -bottom-4 right-0 bdb-handwrite text-lg" style={{ color: '#9A3412', transform: 'rotate(-4deg)' }}>
        ← she's lively today
      </div>
    </div>
  );
}

function TodaysCase({ featured, todayName, todayQuiche }) {
  const reduce = useReducedMotion();
  const dayLabels = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
  const fullDay = dayLabels[todayName] || todayName;

  return (
    <section id="case" className="relative py-16 md:py-24" style={{ background: '#F4ECDA' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold mb-2" style={{ color: '#9A3412' }}>
              {featured?.kicker || "Today's case"}
            </p>
            <h2 className="bdb-display font-black text-4xl md:text-5xl leading-tight" style={{ color: '#23170E' }}>
              What we're baking
              <br />
              <span className="italic font-medium" style={{ color: '#9A3412' }}>today.</span>
            </h2>
          </div>
          <p className="bdb-handwrite text-xl md:text-2xl max-w-md" style={{ color: '#5C4633' }}>
            The case rotates daily — call ahead if you have your eye on something specific.
          </p>
        </div>

        <div className="mb-8 rounded-2xl p-6 md:p-7 border-2 relative overflow-hidden" style={{ borderColor: '#9A3412', background: '#FFFBF2' }}>
          <div className="absolute top-2 right-3 bdb-handwrite text-base" style={{ color: '#9A3412' }}>
            today only ✶
          </div>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#9A3412' }}>
              <Croissant size={26} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] font-bold mb-1" style={{ color: '#D97706' }}>
                {fullDay}'s quiche
              </p>
              <h3 className="bdb-display font-bold text-2xl mb-1" style={{ color: '#23170E' }}>
                {todayQuiche.name}
              </h3>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: '#5C4633' }}>{todayQuiche.desc}</p>
            </div>
            <div className="hidden sm:block bdb-display font-bold text-xl flex-shrink-0" style={{ color: '#9A3412' }}>
              $7.50
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured?.items?.map((item, i) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: reduce ? 0 : (i % 3) * 0.07 }}
              className="rounded-xl p-5 transition-all hover:-translate-y-0.5"
              style={{ background: '#FAF6EE', boxShadow: '0 1px 0 rgba(122,104,86,0.15), 0 8px 20px -12px rgba(35,23,14,0.15)' }}
            >
              <div className="flex justify-between items-baseline gap-3 mb-1.5">
                <h3 className="bdb-display font-bold text-lg leading-tight" style={{ color: '#23170E' }}>
                  {item.name}
                </h3>
                {item.price && (
                  <span className="bdb-display font-bold text-base whitespace-nowrap" style={{ color: '#9A3412' }}>
                    {item.price}
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: '#5C4633' }}>{item.desc}</p>
              {item.tag && (
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bdb-handwrite text-base" style={{ background: '#FBE4C2', color: '#9A3412' }}>
                  {item.tag}
                </span>
              )}
            </motion.div>
          ))}
        </div>

        <p className="text-center mt-10 bdb-handwrite text-2xl" style={{ color: '#9A3412' }}>
          We sell out by 2 — come early ♡
        </p>
      </div>
    </section>
  );
}

function StorySpread({ about, cover }) {
  const body = (about?.body || '').split(/\n\n+/);
  return (
    <section id="story" className="relative py-16 md:py-28 bdb-grain">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#9A3412' }}>
            Chapter Two — The Bakers
          </p>
          <h2 className="bdb-display font-black text-4xl md:text-6xl leading-[1.05] max-w-4xl mx-auto" style={{ color: '#23170E' }}>
            <span className="italic font-medium">{about?.heading || 'A bakery, the way they used to be.'}</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-[1fr_1.4fr] gap-8 md:gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="relative md:sticky md:top-24"
          >
            <div className="relative aspect-[4/5] rounded-md overflow-hidden shadow-2xl" style={{ boxShadow: '0 30px 70px -20px rgba(35,23,14,0.4)' }}>
              <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-3 left-4 right-4 sm:right-auto sm:max-w-[60%] bdb-handwrite text-lg leading-tight px-3 py-2 rotate-[-2deg]" style={{ background: '#FAF6EE', color: '#23170E', border: '1px solid #E5DCC8' }}>
              the kitchen at 5:42 a.m.
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="bdb-display"
          >
            {body.map((para, i) => (
              <p
                key={i}
                className={`leading-[1.7] mb-5 text-[17px] md:text-lg ${i === 0 ? 'first-letter:text-7xl first-letter:font-black first-letter:float-left first-letter:leading-[0.85] first-letter:mr-2 first-letter:mt-1' : ''}`}
                style={{ color: '#3D2918', ...(i === 0 ? { textIndent: 0 } : {}) }}
              >
                {para}
              </p>
            ))}

            <blockquote className="bdb-pull-quote my-10 mx-2 md:mx-6 px-4 md:px-6 py-2 italic font-medium text-2xl md:text-3xl leading-snug border-l-4" style={{ borderColor: '#9A3412', color: '#23170E' }}>
              We have only ever wanted to make bread that tastes like the place it came from.
            </blockquote>

            {about?.signature && (
              <p className="bdb-handwrite text-2xl mt-6" style={{ color: '#9A3412' }}>
                — {about.signature}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CakeStudio() {
  const [tiers, setTiers] = useState(2);
  const [flavor, setFlavor] = useState(CAKE_FLAVORS[0]);
  const [frosting, setFrosting] = useState(CAKE_FROSTINGS[0]);
  const [occasion, setOccasion] = useState('Birthday');
  const [submitted, setSubmitted] = useState(false);

  const occasions = ['Birthday', 'Wedding', 'Anniversary', 'Just-because'];

  function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4500);
  }

  return (
    <section id="cakes" className="relative py-16 md:py-24" style={{ background: '#23170E', color: '#F4ECDD' }}>
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{
             backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(217,119,6,0.4), transparent 40%), radial-gradient(circle at 80% 70%, rgba(154,52,18,0.4), transparent 40%)',
           }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#FB923C' }}>
            The Cake Studio
          </p>
          <h2 className="bdb-display font-black text-4xl md:text-5xl leading-tight">
            Build it — <span className="italic font-medium" style={{ color: '#FB923C' }}>we'll bake it.</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto opacity-80 leading-relaxed">
            Every cake is built from scratch in our kitchen. Sketch it here and we'll be in touch within
            the day. 48 hours notice for our standard menu, two weeks for tiered or wedding cakes.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
          <div className="flex justify-center">
            <CakePreview tiers={tiers} cake={flavor.color} frost={frosting.color} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-7">
            <Field label="Tiers">
              <div className="flex gap-2">
                {[1, 2, 3].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTiers(t)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 bdb-display font-semibold transition-all ${
                      tiers === t ? 'bg-[#FB923C] border-[#FB923C] text-[#23170E]' : 'border-[#5C4633] text-[#F4ECDD] hover:border-[#FB923C]'
                    }`}
                  >
                    {t} tier{t > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Cake flavor">
              <div className="grid grid-cols-2 gap-2">
                {CAKE_FLAVORS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFlavor(f)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-left transition-all ${
                      flavor.id === f.id ? 'border-[#FB923C] bg-[#FB923C]/10' : 'border-[#5C4633] hover:border-[#FB923C]'
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full border border-white/20 flex-shrink-0" style={{ background: f.color }} />
                    <span className="text-sm bdb-display font-medium">{f.label}</span>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Frosting">
              <div className="grid grid-cols-2 gap-2">
                {CAKE_FROSTINGS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFrosting(f)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-left transition-all ${
                      frosting.id === f.id ? 'border-[#FB923C] bg-[#FB923C]/10' : 'border-[#5C4633] hover:border-[#FB923C]'
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full border border-white/20 flex-shrink-0" style={{ background: f.color }} />
                    <span className="text-sm bdb-display font-medium">{f.label}</span>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Occasion">
              <div className="flex flex-wrap gap-2">
                {occasions.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOccasion(o)}
                    className={`px-4 py-2 rounded-full border-2 text-sm bdb-display font-semibold transition-all ${
                      occasion === o ? 'border-[#FB923C] bg-[#FB923C] text-[#23170E]' : 'border-[#5C4633] hover:border-[#FB923C]'
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </Field>

            <button
              type="submit"
              disabled={submitted}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-bold bdb-display text-lg transition-all hover:scale-[1.01] disabled:opacity-80"
              style={{ background: '#FB923C', color: '#23170E' }}
            >
              {submitted ? (
                <><Check size={18} /> We'll email you within the day</>
              ) : (
                <>Send my consult request <Send size={18} /></>
              )}
            </button>
            <p className="text-xs opacity-60 text-center -mt-3">No card needed. First consultation is on the house.</p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: '#FB923C' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function CakePreview({ tiers, cake, frost }) {
  // Stacks 1, 2, or 3 tiers. Each tier has a cake band and a frosting band.
  // Drips on the top tier vary with frosting darkness so dark ganache reads.
  const widths = [180, 140, 100];
  const visibleTiers = widths.slice(0, tiers);
  const tierHeight = 70;
  const baseY = 280;
  const totalHeight = tiers * tierHeight + 60;
  return (
    <div className="relative">
      <svg viewBox="0 0 280 320" className="w-72 md:w-[340px] h-auto" aria-label="A custom cake preview">
        <defs>
          <linearGradient id="bdb-plate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4ECDD" />
            <stop offset="100%" stopColor="#A89683" />
          </linearGradient>
        </defs>

        <ellipse cx="140" cy="298" rx="110" ry="14" fill="url(#bdb-plate)" stroke="rgba(0,0,0,0.2)" />
        <ellipse cx="140" cy="294" rx="110" ry="10" fill="rgba(0,0,0,0.15)" />

        {visibleTiers.map((w, i) => {
          const order = visibleTiers.length - 1 - i;
          const y = baseY - (order + 1) * tierHeight;
          const x = 140 - w / 2;
          return (
            <g key={i}>
              <rect x={x} y={y} width={w} height={tierHeight} fill={cake} />
              <ellipse cx="140" cy={y + tierHeight} rx={w / 2} ry={6} fill={cake} stroke="rgba(0,0,0,0.1)" />
              <rect x={x} y={y + tierHeight - 16} width={w} height="16" fill={frost} />
              <ellipse cx="140" cy={y + tierHeight - 16} rx={w / 2} ry={5} fill={frost} stroke="rgba(0,0,0,0.05)" />
              <ellipse cx="140" cy={y} rx={w / 2} ry={6} fill={frost} />
              {order === visibleTiers.length - 1 && (
                <>
                  <path
                    d={`M ${x + 10} ${y + 4} Q ${x + 12} ${y + 16} ${x + 14} ${y + 6}
                        M ${x + w - 18} ${y + 4} Q ${x + w - 16} ${y + 18} ${x + w - 14} ${y + 6}
                        M ${x + w / 2 - 6} ${y + 4} Q ${x + w / 2 - 4} ${y + 14} ${x + w / 2 - 2} ${y + 6}
                        M ${x + 28} ${y + 4} Q ${x + 30} ${y + 22} ${x + 34} ${y + 6}`}
                    fill={frost}
                    stroke={frost}
                    strokeWidth="3"
                    strokeLinejoin="round"
                  />
                </>
              )}
            </g>
          );
        })}

        <g transform={`translate(140, ${baseY - tiers * tierHeight - 8})`}>
          <line x1="0" y1="-30" x2="0" y2="0" stroke="#9A3412" strokeWidth="1" />
          <path d="M 0 -30 L 6 -34 L 6 -28 Z" fill="#9A3412" />
          <path d="M 0 -30 L -6 -34 L -6 -28 Z" fill="#9A3412" />
        </g>

        {totalHeight > 0 && null}
      </svg>
      <p className="text-center text-xs uppercase tracking-[0.2em] font-bold mt-3" style={{ color: '#FB923C' }}>
        Live preview
      </p>
    </div>
  );
}

function BreadCSA() {
  const [picked, setPicked] = useState({ sourdough: 1, baguette: 0, rye: 0, walnut: 0, whole: 0 });
  const [pickup, setPickup] = useState('Saturday');
  const [frequency, setFrequency] = useState('Weekly');
  const [submitted, setSubmitted] = useState(false);

  const subtotal = useMemo(() => {
    return CSA_LOAVES.reduce((sum, l) => sum + (picked[l.id] || 0) * l.price, 0);
  }, [picked]);
  const totalLoaves = useMemo(() => {
    return Object.values(picked).reduce((s, n) => s + n, 0);
  }, [picked]);

  function bump(id, delta) {
    setPicked((p) => ({ ...p, [id]: Math.max(0, Math.min(6, (p[id] || 0) + delta)) }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (totalLoaves === 0) return;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4500);
  }

  return (
    <section id="csa" className="relative py-16 md:py-24 bdb-grain">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#9A3412' }}>
              Bread CSA
            </p>
            <h2 className="bdb-display font-black text-4xl md:text-5xl leading-[1.05] mb-5" style={{ color: '#23170E' }}>
              Your bread, <span className="italic font-medium" style={{ color: '#9A3412' }}>set aside</span> for you.
            </h2>
            <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#5C4633' }}>
              Pick your loaves, pick your pickup day, and we'll bag them up before the morning rush.
              Members get 10% off pastries and first dibs when something's about to sell out.
            </p>

            <ul className="space-y-2 bdb-display" style={{ color: '#3D2918' }}>
              {[
                'No subscription fees, cancel any time',
                '10% off pastries every visit',
                'First text when a special drops',
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#9A3412' }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="rounded-2xl p-6 md:p-8 border-2" style={{ borderColor: '#9A3412', background: '#FFFBF2' }}>
            <p className="bdb-handwrite text-2xl mb-4" style={{ color: '#9A3412' }}>
              build your weekly bag ↓
            </p>

            <div className="space-y-3 mb-6">
              {CSA_LOAVES.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: '#E5DCC8' }}>
                  <div className="flex-1 min-w-0">
                    <div className="bdb-display font-bold" style={{ color: '#23170E' }}>{l.name}</div>
                    <div className="text-xs" style={{ color: '#7A6856' }}>${l.price.toFixed(2)} each</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => bump(l.id, -1)}
                      aria-label={`One fewer ${l.name}`}
                      className="w-8 h-8 rounded-full border-2 flex items-center justify-center hover:bg-[#9A3412] hover:text-white hover:border-[#9A3412] transition-colors"
                      style={{ borderColor: '#9A3412', color: '#9A3412' }}
                    >
                      −
                    </button>
                    <span className="w-8 text-center bdb-display font-bold text-lg tabular-nums" style={{ color: '#23170E' }}>
                      {picked[l.id] || 0}
                    </span>
                    <button
                      type="button"
                      onClick={() => bump(l.id, 1)}
                      aria-label={`One more ${l.name}`}
                      className="w-8 h-8 rounded-full border-2 flex items-center justify-center hover:bg-[#9A3412] hover:text-white hover:border-[#9A3412] transition-colors"
                      style={{ borderColor: '#9A3412', color: '#9A3412' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <Selector
                label="Pickup day"
                value={pickup}
                onChange={setPickup}
                options={['Tuesday', 'Thursday', 'Saturday', 'Sunday']}
              />
              <Selector
                label="Frequency"
                value={frequency}
                onChange={setFrequency}
                options={['Weekly', 'Bi-weekly', 'Monthly']}
              />
            </div>

            <div className="flex items-end justify-between mb-5 pb-5 border-t pt-5" style={{ borderColor: '#E5DCC8' }}>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: '#7A6856' }}>
                  {frequency} total
                </div>
                <div className="bdb-display font-black text-3xl" style={{ color: '#9A3412' }}>
                  ${subtotal.toFixed(2)}
                </div>
              </div>
              <div className="text-right text-sm" style={{ color: '#5C4633' }}>
                {totalLoaves} loaf{totalLoaves !== 1 ? 'ves' : ''} per pickup
              </div>
            </div>

            <button
              type="submit"
              disabled={totalLoaves === 0 || submitted}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-bold bdb-display text-lg text-white transition-all disabled:opacity-50 hover:bg-[#7A2810]"
              style={{ background: '#9A3412' }}
            >
              {submitted ? (
                <><Check size={18} /> We'll save your bag this {pickup}</>
              ) : totalLoaves === 0 ? (
                <>Pick a loaf to start</>
              ) : (
                <>Sign me up <ArrowRight size={18} /></>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Selector({ label, value, onChange, options }) {
  return (
    <div>
      <span className="block text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: '#9A3412' }}>
        {label}
      </span>
      <div className="grid grid-cols-2 gap-1.5">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`px-2.5 py-1.5 rounded-md border-2 text-sm bdb-display font-semibold transition-all ${
              value === o ? 'bg-[#9A3412] text-white border-[#9A3412]' : 'border-[#E5DCC8] text-[#3D2918] hover:border-[#9A3412]'
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function GalleryStrip({ gallery }) {
  if (!gallery?.length) return null;
  const items = gallery.slice(0, 4);
  const captions = ['the morning lamination', 'crumb shot', 'the back counter', 'window light'];
  const rotations = ['-rotate-2', 'rotate-1', '-rotate-1', 'rotate-2'];
  return (
    <section className="py-16 md:py-20" style={{ background: '#F4ECDA' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <p className="text-xs uppercase tracking-[0.25em] font-bold mb-3 text-center" style={{ color: '#9A3412' }}>
          Field Notes
        </p>
        <h2 className="bdb-display font-black text-3xl md:text-4xl text-center mb-10" style={{ color: '#23170E' }}>
          A few photos <span className="italic font-medium" style={{ color: '#9A3412' }}>from the kitchen</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-7">
          {items.map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`bg-white p-2 pb-8 shadow-md hover:shadow-xl transition-shadow ${rotations[i % rotations.length]}`}
              style={{ borderRadius: 2 }}
            >
              <div className="aspect-square overflow-hidden bg-[#F4ECDA]">
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <p className="bdb-handwrite text-base md:text-lg text-center mt-2 leading-tight" style={{ color: '#23170E' }}>
                {captions[i % captions.length]}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Polaroids({ testimonials }) {
  const [index, setIndex] = useState(0);
  if (!testimonials?.length) return null;
  const total = testimonials.length;

  return (
    <section className="py-16 md:py-24 bdb-grain">
      <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#9A3412' }}>
          From the Guest Book
        </p>
        <h2 className="bdb-display font-black text-3xl md:text-5xl mb-12" style={{ color: '#23170E' }}>
          Notes <span className="italic font-medium" style={{ color: '#9A3412' }}>left at the counter</span>
        </h2>

        <div className="relative h-[420px] md:h-[400px] flex items-center justify-center">
          {testimonials.map((t, i) => {
            const offset = i - index;
            const isActive = offset === 0;
            const isVisible = Math.abs(offset) <= 1;
            const rotate = (i % 3 === 0 ? -3 : i % 3 === 1 ? 2 : -1) + offset * 4;
            return (
              <motion.div
                key={i}
                animate={{
                  x: offset * 30,
                  y: Math.abs(offset) * 8,
                  rotate,
                  scale: isActive ? 1 : 0.92,
                  opacity: isVisible ? 1 : 0,
                  zIndex: 10 - Math.abs(offset),
                }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="absolute bg-white shadow-xl px-5 pt-5 pb-10 max-w-md w-[88%]"
                style={{ pointerEvents: isActive ? 'auto' : 'none' }}
              >
                <div className="flex justify-center gap-0.5 mb-3">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} size={16} className="fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <p className="bdb-display italic text-base md:text-lg leading-relaxed" style={{ color: '#23170E' }}>
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="bdb-handwrite text-xl mt-4" style={{ color: '#9A3412' }}>
                  — {t.author}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-3 mt-10">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            aria-label="Previous note"
            className="w-10 h-10 rounded-full border-2 flex items-center justify-center hover:bg-[#9A3412] hover:text-white hover:border-[#9A3412] transition-colors"
            style={{ borderColor: '#9A3412', color: '#9A3412' }}
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Note ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === index ? 'w-6' : 'w-2'}`}
                style={{ background: i === index ? '#9A3412' : 'rgba(154,52,18,0.3)' }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % total)}
            aria-label="Next note"
            className="w-10 h-10 rounded-full border-2 flex items-center justify-center hover:bg-[#9A3412] hover:text-white hover:border-[#9A3412] transition-colors"
            style={{ borderColor: '#9A3412', color: '#9A3412' }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function Visit({ demo, phoneRaw }) {
  return (
    <section id="visit" className="relative py-16 md:py-24" style={{ background: '#23170E', color: '#F4ECDD' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: '#FB923C' }}>
              Come See Us
            </p>
            <h2 className="bdb-display font-black text-4xl md:text-5xl leading-tight mb-6">
              The seat by the <span className="italic font-medium" style={{ color: '#FB923C' }}>window</span> is open.
            </h2>
            <p className="text-lg leading-relaxed opacity-80 mb-8">
              We're a five-minute walk from the park, in the building with the blue front door.
              Stay as long as you like.
            </p>

            <Storefront />
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl p-6 border-2 border-[#5C4633]" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: '#FB923C' }}>
                Hours
              </p>
              <ul className="divide-y divide-[#5C4633]">
                {demo.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between py-2.5">
                    <span className="bdb-display font-semibold inline-flex items-center gap-2">
                      <Clock size={14} style={{ color: '#FB923C' }} /> {h.day}
                    </span>
                    <span className="opacity-80">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl p-6 border-2 border-[#5C4633]" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: '#FB923C' }}>
                Reach Us
              </p>
              <ul className="space-y-3">
                <li>
                  <a href={`tel:${phoneRaw}`} className="flex items-center gap-3 hover:text-[#FB923C] transition-colors">
                    <Phone size={16} className="flex-shrink-0" style={{ color: '#FB923C' }} />
                    <span className="bdb-display font-semibold">{demo.contact.phone}</span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${demo.contact.email}`} className="flex items-center gap-3 hover:text-[#FB923C] transition-colors break-all">
                    <Mail size={16} className="flex-shrink-0" style={{ color: '#FB923C' }} />
                    <span className="bdb-display font-semibold">{demo.contact.email}</span>
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="flex-shrink-0 mt-1" style={{ color: '#FB923C' }} />
                  <span className="bdb-display font-semibold">{demo.contact.address}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl p-6 text-[#23170E]" style={{ background: '#FB923C' }}>
              <Heart size={20} className="mb-2" />
              <p className="bdb-display font-bold text-lg leading-snug">
                Bringing kids? Books and crayons live in the basket by the door.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Storefront() {
  return (
    <svg viewBox="0 0 320 200" className="w-full max-w-md" aria-label="Illustration of the bakery storefront">
      <rect x="20" y="40" width="280" height="130" fill="#F4ECDD" stroke="#9A3412" strokeWidth="2" />
      <rect x="20" y="30" width="280" height="14" fill="#9A3412" />
      <text x="160" y="41" textAnchor="middle" fontSize="9" fontFamily="Fraunces, serif" fontWeight="800" letterSpacing="2" fill="#FAF6EE">
        BLUE DOOR BAKERY
      </text>

      <rect x="120" y="80" width="50" height="90" fill="#1E3A5F" />
      <rect x="125" y="85" width="40" height="80" fill="#2C5282" />
      <circle cx="160" cy="125" r="2" fill="#FBBF24" />
      <rect x="142" y="140" width="6" height="20" fill="#1E3A5F" />

      <rect x="40" y="80" width="60" height="60" fill="#FFEBCC" stroke="#9A3412" strokeWidth="1.5" />
      <line x1="70" y1="80" x2="70" y2="140" stroke="#9A3412" strokeWidth="1" />
      <line x1="40" y1="110" x2="100" y2="110" stroke="#9A3412" strokeWidth="1" />
      <circle cx="55" cy="92" r="5" fill="#D97706" />
      <circle cx="85" cy="95" r="6" fill="#92400E" />
      <ellipse cx="55" cy="125" rx="8" ry="4" fill="#F4E4C4" />
      <ellipse cx="85" cy="125" rx="8" ry="5" fill="#A0522D" />

      <rect x="190" y="80" width="60" height="60" fill="#FFEBCC" stroke="#9A3412" strokeWidth="1.5" />
      <line x1="220" y1="80" x2="220" y2="140" stroke="#9A3412" strokeWidth="1" />
      <line x1="190" y1="110" x2="250" y2="110" stroke="#9A3412" strokeWidth="1" />
      <ellipse cx="205" cy="95" rx="6" ry="4" fill="#D97706" />
      <ellipse cx="235" cy="93" rx="7" ry="5" fill="#B45309" />
      <circle cx="205" cy="125" r="5" fill="#92400E" />
      <ellipse cx="235" cy="125" rx="7" ry="4" fill="#F4E4C4" />

      <rect x="40" y="148" width="240" height="22" fill="#FAF6EE" stroke="#9A3412" strokeWidth="1" />
      <text x="160" y="163" textAnchor="middle" fontSize="9" fontFamily="Caveat, cursive" fill="#9A3412">
        ✶ today: rye, sourdough, baguettes ✶
      </text>

      <rect x="100" y="14" width="120" height="20" fill="none" stroke="#9A3412" strokeWidth="1.5" />
      <text x="160" y="27" textAnchor="middle" fontSize="10" fontFamily="Caveat, cursive" fontWeight="600" fill="#9A3412">
        OPEN — come in!
      </text>

      <rect x="0" y="170" width="320" height="30" fill="#5C4633" />
      <rect x="0" y="168" width="320" height="3" fill="#9A3412" />
    </svg>
  );
}

function Footer({ demo, phoneRaw }) {
  return (
    <footer className="py-10 md:py-12" style={{ background: '#FAF6EE', borderTop: '1px solid #E5DCC8' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-full flex items-center justify-center text-[#FAF6EE]" style={{ background: '#9A3412' }}>
                <Wheat size={16} />
              </span>
              <div>
                <div className="bdb-display font-bold text-lg leading-tight" style={{ color: '#23170E' }}>{demo.name}</div>
                <div className="bdb-handwrite text-base -mt-1" style={{ color: '#9A3412' }}>est. 2014</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: '#5C4633' }}>
              A neighborhood bakery built on butter, time, and good company.
            </p>
          </div>

          <div className="bdb-display">
            <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: '#9A3412' }}>Find us</p>
            <ul className="space-y-1.5 text-sm" style={{ color: '#3D2918' }}>
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 flex-shrink-0" style={{ color: '#9A3412' }} />
                {demo.contact.address}
              </li>
              <li className="flex items-start gap-2">
                <Phone size={14} className="mt-1 flex-shrink-0" style={{ color: '#9A3412' }} />
                <a href={`tel:${phoneRaw}`} className="hover:text-[#9A3412]">{demo.contact.phone}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={14} className="mt-1 flex-shrink-0" style={{ color: '#9A3412' }} />
                <a href={`mailto:${demo.contact.email}`} className="hover:text-[#9A3412] break-all">{demo.contact.email}</a>
              </li>
            </ul>
          </div>

          <div className="bdb-display">
            <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: '#9A3412' }}>Wander to</p>
            <ul className="space-y-1.5 text-sm" style={{ color: '#3D2918' }}>
              <li><a href="#hilde" className="hover:text-[#9A3412]">Meet Hilde, our starter</a></li>
              <li><a href="#case" className="hover:text-[#9A3412]">Today's case</a></li>
              <li><a href="#cakes" className="hover:text-[#9A3412]">Custom cake studio</a></li>
              <li><a href="#csa" className="hover:text-[#9A3412]">Bread CSA</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-2 text-xs" style={{ borderColor: '#E5DCC8', color: '#7A6856' }}>
          <p>&copy; {new Date().getFullYear()} {demo.name}. Baked with butter and patience.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="font-semibold hover:underline" style={{ color: '#9A3412' }}>Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default BlueDoorBakeryDemo;
